import { useState } from 'react';

export const storage = {
  SESSION: 'sessionStorage',
  LOCAL: 'localStorage',
} as const;

export const storageKeys = {
  EMAIL: 'email',
  PACKAGE_ID: 'packageId',
  SUBSCRIPTION_ID: 'subscriptionId',
  TOKEN: 'accessToken',
  SUMMARY_PRICES: 'summaryPrices',
  SUMMARY_PAYLOAD: 'summaryPayload',
  PROMO_PLAN_ID: 'promoPlanId',
  PROMO_CODES: 'promoCodes',
  PROMO_CODES_APPLY: 'promoCodesApply',
  PLAN_SETTINGS: 'planSettings',
  COUNTRY_CODE: 'countryCode',
  SUMMARY_CHECKOUT_TEXT: 'summaryCheckoutText',
  SUMMARY_DESCRIPTION: 'summaryDescription',
  LOGIN_URL: 'loginUrl',
  PARTNER_ID: 'partnerId',
  PRODUCT_FAMILIES: 'productFamilies',
  EXISTING_USER: 'existingUser',
  QUERY_PARAMS: 'queryParams',
  MARKETING_INFO: 'marketingInfo:',
  WEBINAR_WIZARD: 'webinarWizard:',
  PACKAGE_DATA: 'packageData',
} as const;

export type StorageType = typeof storage[keyof typeof storage];

export const useStorage = <T = any>(
  storage: StorageType,
  keyName: string,
  defaultValue?: T
): [T, (value: T | ((value: T) => T)) => void] => {
  const [storedValue, setStoredValue] = useState<T>(() => {
    const storedDefaultValue = defaultValue || null;
    try {
      const value = window[storage].getItem(keyName);

      if (value) {
        return JSON.parse(value);
      } else {
        window[storage].setItem(keyName, JSON.stringify(storedDefaultValue));
        return storedDefaultValue;
      }
    } catch (err) {
      return storedDefaultValue;
    }
  });

  const setValue = (value: T | ((value: T) => T)) => {
    const newValue = value instanceof Function ? value(storedValue) : value;
    try {
      window[storage].setItem(keyName, JSON.stringify(newValue));
    } finally {
      setStoredValue(newValue);
    }
  };

  return [storedValue, setValue];
};
